import { useEffect, useState } from "react";

export type ProductFilterValues = {
  q?: string;
  min?: number;
  max?: number;
  sort?: "new" | "low" | "high" | "popular";
};

export function ProductFilters({
  values,
  onChange,
  onReset,
  showSearch = true,
  className,
}: {
  values: ProductFilterValues;
  onChange: (patch: Partial<ProductFilterValues>) => void;
  onReset?: () => void;
  showSearch?: boolean;
  className?: string;
}) {
  const [qInput, setQInput] = useState(values.q ?? "");
  const [minInput, setMinInput] = useState<string>(values.min != null ? String(values.min) : "");
  const [maxInput, setMaxInput] = useState<string>(values.max != null ? String(values.max) : "");

  useEffect(() => { setQInput(values.q ?? ""); }, [values.q]);
  useEffect(() => { setMinInput(values.min != null ? String(values.min) : ""); }, [values.min]);
  useEffect(() => { setMaxInput(values.max != null ? String(values.max) : ""); }, [values.max]);

  const applyPrice = () => onChange({
    min: minInput ? Number(minInput) : undefined,
    max: maxInput ? Number(maxInput) : undefined,
  });

  const hasAny = values.q || values.min != null || values.max != null;

  return (
    <div className={`mt-8 flex flex-col sm:flex-row sm:flex-wrap sm:items-center sm:justify-between gap-4 border-b border-border pb-6 ${className ?? ""}`}>
      {showSearch && (
        <form
          onSubmit={(e) => { e.preventDefault(); onChange({ q: qInput || undefined }); }}
          className="grid w-full grid-cols-[minmax(0,1fr)_auto] gap-2 sm:flex sm:flex-1 sm:min-w-[260px] sm:max-w-xl"
        >
          <input
            value={qInput}
            onChange={(e) => setQInput(e.target.value)}
            placeholder="Išči po izdelkih in trgovinah…"
            aria-label="Iskanje po izdelkih in trgovinah"
            maxLength={200}
            className="flex-1 min-w-0 bg-background min-h-11 border border-border px-4 py-2.5 text-sm focus:outline-none focus:border-foreground"
          />
          <button type="submit" className="min-h-11 shrink-0 bg-primary text-primary-foreground px-4 py-2.5 text-sm hover:opacity-90 sm:px-5">
            Išči
          </button>
          {hasAny && onReset && (
            <button type="button" onClick={onReset}
              className="min-h-11 col-span-2 shrink-0 border border-border px-4 py-2.5 text-sm hover:bg-muted sm:col-span-1">
              Počisti
            </button>
          )}
        </form>
      )}
      <div className="grid w-full gap-3 sm:flex sm:w-auto sm:flex-wrap sm:items-center">
        <div className="grid grid-cols-[auto_minmax(0,1fr)_auto_minmax(0,1fr)_auto] items-center gap-1 text-sm sm:flex">
          <span className="text-muted-foreground text-xs uppercase tracking-wider">Cena</span>
          <input type="number" min="0" placeholder="od" value={minInput}
            aria-label="Najnižja cena"
            onChange={(e) => setMinInput(e.target.value)} onBlur={applyPrice}
            onKeyDown={(e) => e.key === "Enter" && applyPrice()}
            className="w-full min-w-0 bg-background border border-border min-h-11 px-2 py-2 text-sm sm:min-h-0 sm:w-16 sm:py-1" />
          <span>–</span>
          <input type="number" min="0" placeholder="do" value={maxInput}
            aria-label="Najvišja cena"
            onChange={(e) => setMaxInput(e.target.value)} onBlur={applyPrice}
            onKeyDown={(e) => e.key === "Enter" && applyPrice()}
            className="w-full min-w-0 bg-background border border-border min-h-11 px-2 py-2 text-sm sm:min-h-0 sm:w-16 sm:py-1" />
          <span className="text-muted-foreground text-xs">€</span>
        </div>
        <select value={values.sort ?? "popular"}
          aria-label="Razvrsti po"
          onChange={(e) => onChange({ sort: e.target.value as "new" | "low" | "high" | "popular" })}
          className="w-full min-w-0 bg-background border border-border min-h-11 px-3 py-2 text-sm sm:min-h-0 sm:w-auto sm:flex-none sm:py-1.5">
          <option value="popular">Najbolj priljubljeni</option>
          <option value="new">Najnovejše</option>
          <option value="low">Cena: nizka → visoka</option>
          <option value="high">Cena: visoka → nizka</option>
        </select>
      </div>
    </div>
  );
}
